// src/pages/client/ProvidersPage.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  X,
  Filter,
  MapPin,
  Star,
  DollarSign,
  Briefcase,
  Award,
  CheckCircle,
  Navigation,
  Eye,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sliders,
  SortAsc,
  Grid,
  Wrench,
  Users,
  Globe,
  Clock,
  Loader2,
  AlertCircle,
  ChevronLeft
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import useIPLocation from '../../hooks/useIPLocation';
import colors from '../../constants/colors';
import {
  getAllProviders,
  advancedSearch,
  searchNearby,
  getNearbyProviders,
  autocompleteSearch,
  getSearchFilters,
  searchByAvailability,
  getPopularProviders,
  getByRatingRange,
  getByPriceRange,
  getByServiceType,
  fuzzySearch,
  getSearchStatistics,
  getRandomProviders,
  getRandomProvidersByServiceType,
  getRandomFeaturedProviders,
  clearSearchCache,
  selectSearchResults,
  selectPopularProviders,
  selectAutocompleteResults,
  selectNearbyProviders,
  selectAvailabilityResults,
  selectFuzzyResults,
  selectRandomResults,
  selectSearchFilters,
  selectSelectedFilters,
  selectStatistics,
  selectPagination,
  selectSearchLoading,
  selectNearbyLoading,
  selectPopularLoading,
  selectFiltersLoading,
  selectStatisticsLoading,
  selectRandomLoading,
  selectSearchError,
  selectNearbyError,
  selectPopularError,
  selectFiltersError,
  selectRandomError,
  selectIsSearching,
  selectShowAdvancedSearch,
  selectCacheKey,
  selectLastSearchTimestamp,
  selectHasResults,
  selectResultsCount,
  selectTotalResults,
  selectHasMoreResults,
  selectCurrentPage,
  selectTotalPages,
  selectTopRatedProviders,
  selectLowestPriceProviders,
  selectFeaturedProviders,
  selectAverageRating,
  selectRandomProviders,
  resetSearchState,
  clearSearchErrors,
  setSearchParams,
  setPagination,
  setFilters,
  clearFilters,
  toggleAdvancedSearch,
  setSearching,
  appendResults,
  clearResults,
  setCacheKey
} from '../../store/slices/serviceProviderSearchSlice';

// Import service types from constants
import { 
  SERVICE_TYPES_ARRAY as serviceTypes,
  SERVICE_CATEGORIES,
  getServiceTypesByCategory,
  getCategoryForServiceType
} from '../../constants/serviceTypes';
import { categoryIcons, categoryColors } from '../../constants/serviceIcons';

// Import CSS Module
import styles from '../../styles/ProvidersPage.module.css';
import formatAddress from '../../utils/addressHelper';

// ======================================================
// CONSTANTS
// ======================================================
const MAX_DISTANCE_NEAR_ME = 100;
const MIN_RATING_FOR_TOP_RATED = 4.5;
const DISPLAY_CATEGORIES_COUNT = 12; // Show 12 categories initially

// ======================================================
// HAITIAN CITIES
// ======================================================
const HAITIAN_CITIES = [
  'port-au-prince', 'cap-haïtien', 'cap-haitien', 'jacmel', 'gonaïves', 'gonaives',
  'les cayes', 'jérémie', 'jeremie', 'hinche', 'port-de-paix', 'petit-goâve',
  'petit-goave', 'miragôane', 'miragowane', 'saint-marc', 'saint marc',
  'croix-des-bouquets', 'croix des bouquets', 'delmas', 'pétion-ville', 'pétionville',
  'carrefour', 'kenscoff', 'leogâne', 'leogane', 'grand-goâve', 'grand-goave'
];

// ======================================================
// HELPER: Calculate distance between two coordinates (in miles)
// ======================================================
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance;
};

// ======================================================
// HELPER: Format distance display
// ======================================================
const formatDistanceDisplay = (distance) => {
  if (distance === null || distance === undefined || distance === Infinity) {
    return 'N/A';
  }
  
  if (distance < 0.1) {
    return '< 0.1 mi';
  } else if (distance < 1) {
    return `${(distance * 5280).toFixed(0)} ft`;
  } else if (distance < 10) {
    return `${distance.toFixed(1)} mi`;
  } else {
    return `${distance.toFixed(0)} mi`;
  }
};

// ======================================================
// SEPARATE COMPONENT FOR PROVIDER CARD
// ======================================================
const ProviderCard = ({ item, onPress, onDirection }) => {
  const { translatedText: translatedServiceType } = useTranslation(item?.serviceType || '');
  const { translatedText: translatedBusinessName } = useTranslation(item?.businessName || item?.name || '');
  const { translatedText: directions } = useTranslation('Directions');
  const { translatedText: viewProfile } = useTranslation('View Profile');
  const { translatedText: featuredLabel } = useTranslation('Featured');
  const { translatedText: yrs } = useTranslation('yrs');

  if (!item) return null;

  // Check if provider is in Haiti using address
  const isInHaiti = item.address?.country?.toLowerCase() === 'haiti' || 
                     item.address?.country?.toLowerCase() === 'ht' ||
                     item.address?.country?.toLowerCase() === 'haïti' ||
                     HAITIAN_CITIES.some(city => 
                       item.address?.city?.toLowerCase().includes(city)
                     );

  const locationDisplay = isInHaiti 
    ? '🇭🇹 Haiti'
    : item.address?.country 
      ? `🌍 ${item.address.country}`
      : '🌍 Diaspora';

  const imageUrl = item.profileImage?.url || 'https://via.placeholder.com/80';

  // Determine if we should show distance
  const showDistance = item.distance !== null && 
                       item.distance !== undefined && 
                       item.distance !== 'N/A' &&
                       item.distance !== Infinity;

  return (
    <div className={styles.providerCard} onClick={() => onPress(item)}>
      <div className={styles.providerCardImageWrapper}>
        <img 
          src={imageUrl} 
          alt={translatedBusinessName}
          className={styles.providerCardImage}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/80';
          }}
        />
        {item.isFeatured && (
          <div className={styles.featuredBadge}>
            <Award size={14} />
            <span>{featuredLabel}</span>
          </div>
        )}
      </div>
      <div className={styles.providerCardContent}>
        <div className={styles.providerCardHeader}>
          <div className={styles.providerNameWrapper}>
            <h3 className={styles.providerName}>{translatedBusinessName}</h3>
          </div>
          <div className={styles.providerServiceType}>{translatedServiceType || item.serviceType || 'Service'}</div>
        </div>
        
        <div className={styles.providerCardMeta}>
          <div className={styles.metaItem}>
            <MapPin size={14} />
            <span>{item.address?.city || 'Unknown'}, {item.address?.country || ''}</span>
          </div>
          {showDistance && (
            <div className={styles.metaItem}>
              <Navigation size={14} />
              <span>{formatDistanceDisplay(item.distance)}</span>
            </div>
          )}
          <div className={styles.communityTag}>
            {locationDisplay}
          </div>
        </div>

        <div className={styles.providerCardRating}>
          <div className={styles.stars}>
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                size={16} 
                color={i < Math.floor(item.rating || 0) ? colors.accentGold : colors.gray300}
                fill={i < Math.floor(item.rating || 0) ? colors.accentGold : 'none'}
              />
            ))}
          </div>
          <span className={styles.ratingValue}>{item.rating || 0}</span>
          <span className={styles.reviewCount}>({item.totalReviews || 0} reviews)</span>
        </div>

        <div className={styles.providerCardTags}>
          <div className={styles.tag}>
            <Briefcase size={14} />
            <span>{item.yearsOfExperience || 0} {yrs}</span>
          </div>
          {item.hourlyRate && (
            <div className={styles.tag}>
              <DollarSign size={14} />
              <span>${item.hourlyRate}/hr</span>
            </div>
          )}
        </div>

        <div className={styles.providerCardActions}>
          <button 
            className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
            onClick={(e) => { e.stopPropagation(); onDirection(item); }}
          >
            <Navigation size={18} />
            <span>{directions}</span>
          </button>
          <button 
            className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
            onClick={(e) => { e.stopPropagation(); onPress(item); }}
          >
            <span>{viewProfile}</span>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// SEPARATE COMPONENT FOR CATEGORY CHIP
// ======================================================
const CategoryChip = ({ category, selectedCategory, setSelectedCategory, providerCount }) => {
  const { translatedText: categoryName } = useTranslation(category);
  const icon = categoryIcons[category] || '📋';
  const isSelected = selectedCategory === category;
  const color = categoryColors[category] || colors.primary;
  
  return (
    <button
      className={`${styles.categoryChip} ${isSelected ? styles.active : ''}`}
      onClick={() => setSelectedCategory(isSelected ? null : category)}
      style={isSelected ? { borderColor: color, backgroundColor: color + '20' } : {}}
    >
      <span className={styles.categoryChipEmoji}>{icon}</span>
      <span className={styles.categoryChipText}>{categoryName}</span>
      {providerCount !== undefined && providerCount > 0 && (
        <span className={styles.categoryChipCount}>{providerCount}</span>
      )}
    </button>
  );
};

// ======================================================
// MAIN PROVIDERS PAGE COMPONENT
// ======================================================
const ProvidersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state from serviceProviderSearchSlice
  const results = useSelector(selectSearchResults) || [];
  const isLoading = useSelector(selectSearchLoading) || false;
  const error = useSelector(selectSearchError) || null;
  const pagination = useSelector(selectPagination) || {};
  const hasMore = useSelector(selectHasMoreResults) || false;
  const isSearching = useSelector(selectIsSearching) || false;
  const totalResults = useSelector(selectTotalResults) || 0;

  // ======================================================
  // IP LOCATION HOOK
  // ======================================================
  const {
    position: ipLocation,
    error: locationError,
    loading: locationLoading,
    source: locationSource,
    isFallback: isFallbackLocation,
    hasLocation: hasIPLocation,
    locationString,
    refreshLocation,
    formatLocation,
    getDistance
  } = useIPLocation({
    autoLoad: true,
    maxRetries: 2,
    retryDelay: 1000,
    cacheEnabled: true
  });

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(location.state?.category || null);
  const [selectedFilter, setSelectedFilter] = useState(location.state?.filter || 'all');
  const [selectedCommunity, setSelectedCommunity] = useState('all');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isLocationReady, setIsLocationReady] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [page, setPage] = useState(1);

  const { currentLanguage } = useLanguage();

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: findTechnicians } = useTranslation('Find providers');
  const { translatedText: searchPlaceholder } = useTranslation('Search by name, service, or specialty...');
  const { translatedText: filtersLabel } = useTranslation('Filters');
  const { translatedText: sortByLabel } = useTranslation('Sort By');
  const { translatedText: applyFilters } = useTranslation('Apply Filters');
  const { translatedText: resetFilters } = useTranslation('Reset All Filters');
  const { translatedText: noTechniciansFound } = useTranslation('No providers found');
  const { translatedText: adjustSearch } = useTranslation('Try adjusting your search or filters');
  const { translatedText: techniciansFounds } = useTranslation('providers found');
  const { translatedText: techniciansFound } = useTranslation('provider found');
  const { translatedText: all } = useTranslation('All');
  const { translatedText: topRated } = useTranslation('Top Rated');
  const { translatedText: nearest } = useTranslation('Nearest');
  const { translatedText: featured } = useTranslation('Featured');
  const { translatedText: allCommunities } = useTranslation('All Communities');
  const { translatedText: haitiLabel } = useTranslation('Haiti');
  const { translatedText: diasporaLabel } = useTranslation('Diaspora');
  const { translatedText: highestRating } = useTranslation('Highest Rating');
  const { translatedText: nearestFirst } = useTranslation('Nearest First');
  const { translatedText: mostExperienced } = useTranslation('Most Experienced');
  const { translatedText: lowestPrice } = useTranslation('Lowest Price');
  const { translatedText: highestPrice } = useTranslation('Highest Price');
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');
  const { translatedText: showLess } = useTranslation('Show Less');
  const { translatedText: showMore } = useTranslation('Show More');
  const { translatedText: moreCategories } = useTranslation('More Categories');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // Filter options with translations
  const filterOptions = [
    { id: 'all', label: all, icon: Grid },
    { id: 'top_rated', label: topRated, icon: Star },
    { id: 'nearest', label: nearest, icon: MapPin },
    { id: 'featured', label: featured, icon: Award }
  ];

  // Community filter options with translations
  const communityOptions = [
    { id: 'all', label: allCommunities },
    { id: 'Haiti', label: '🇭🇹 ' + haitiLabel },
    { id: 'Diaspora', label: '🌍 ' + diasporaLabel }
  ];

  // Sort options with translations
  const sortOptions = [
    { id: 'rating', label: highestRating },
    { id: 'distance', label: nearestFirst },
    { id: 'experience', label: mostExperienced },
    { id: 'price_low', label: lowestPrice },
    { id: 'price_high', label: highestPrice }
  ];

  const [sortBy, setSortBy] = useState('distance'); // Changed default to 'distance'

  // Determine which categories to display
  const displayedCategories = useMemo(() => {
    if (showAllCategories) {
      return serviceTypes;
    }
    return serviceTypes.slice(0, DISPLAY_CATEGORIES_COUNT);
  }, [showAllCategories]);

  // ======================================================
  // Set location ready when location is loaded
  // ======================================================
  useEffect(() => {
    if (hasIPLocation || locationError) {
      setIsLocationReady(true);
    }
  }, [hasIPLocation, locationError]);

  // ======================================================
  // HELPER: Get provider coordinates
  // ======================================================
  const getProviderCoordinates = (provider) => {
    try {
      if (provider.location?.coordinates) {
        const coords = provider.location.coordinates;
        if (Array.isArray(coords) && coords.length === 2) {
          return { lat: coords[1], lng: coords[0] };
        }
      }
      if (provider.coordinates) {
        return { lat: provider.coordinates.lat, lng: provider.coordinates.lng };
      }
      return null;
    } catch (error) {
      console.warn('Error getting provider coordinates:', error);
      return null;
    }
  };

  // ======================================================
  // CHECK IF PROVIDER IS IN HAITI
  // ======================================================
  const isProviderInHaiti = useCallback((provider) => {
    if (!provider || !provider.address) return false;
    
    const country = provider.address.country?.toLowerCase() || '';
    const city = provider.address.city?.toLowerCase() || '';
    
    if (country === 'haiti' || country === 'ht' || country === 'haïti') {
      return true;
    }
    
    if (HAITIAN_CITIES.some(haitianCity => city.includes(haitianCity))) {
      return true;
    }
    
    return false;
  }, []);

  // ======================================================
  // PROCESS PROVIDERS WITH DISTANCE
  // ======================================================
  const processedProviders = useMemo(() => {
    if (!Array.isArray(results) || results.length === 0) {
      return [];
    }

    return results
      .filter(provider => provider !== null && provider !== undefined)
      .map(provider => {
        let distance = null;
        let hasValidCoords = false;

        const coords = getProviderCoordinates(provider);
        
        if (coords && ipLocation && ipLocation.latitude && ipLocation.longitude) {
          hasValidCoords = true;
          try {
            distance = calculateDistance(
              ipLocation.latitude,
              ipLocation.longitude,
              coords.lat,
              coords.lng
            );
          } catch (err) {
            console.warn('Distance calculation error:', err);
            distance = null;
          }
        }

        const isInHaiti = isProviderInHaiti(provider);

        return {
          ...provider,
          distance,
          distanceDisplay: formatDistanceDisplay(distance),
          hasValidCoords,
          isInHaiti
        };
      });
  }, [results, ipLocation, isProviderInHaiti]);

  // ======================================================
  // GET CATEGORY COUNTS FROM PROVIDERS
  // ======================================================
  const categoryCounts = useMemo(() => {
    const counts = {};
    serviceTypes.forEach(type => {
      counts[type] = processedProviders.filter(p => p.serviceType === type).length;
    });
    return counts;
  }, [processedProviders]);

  // ======================================================
  // FILTER PROVIDERS
  // ======================================================
  const filteredProviders = useMemo(() => {
    if (!Array.isArray(processedProviders) || processedProviders.length === 0) {
      return [];
    }

    let filtered = [...processedProviders];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(provider => 
        (provider.businessName || '').toLowerCase().includes(query) ||
        (provider.serviceType || '').toLowerCase().includes(query) ||
        (provider.name || '').toLowerCase().includes(query) ||
        (provider.description || '').toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(provider => provider.serviceType === selectedCategory);
    }
    
    // Community filter
    if (selectedCommunity !== 'all') {
      const isHaiti = selectedCommunity === 'Haiti';
      filtered = filtered.filter(provider => provider.isInHaiti === isHaiti);
    }
    
    // Special filters - including nearest
    switch(selectedFilter) {
      case 'top_rated':
        filtered = filtered.filter(provider => (provider.rating || 0) >= MIN_RATING_FOR_TOP_RATED);
        break;
      case 'nearest':
        // Filter out providers without valid coordinates or distance
        filtered = filtered.filter(provider => 
          provider.hasValidCoords && 
          provider.distance !== null && 
          provider.distance !== undefined &&
          provider.distance !== Infinity
        );
        // Sort by distance (closest first)
        filtered = filtered.sort((a, b) => {
          const distA = a.distance || 999999;
          const distB = b.distance || 999999;
          return distA - distB;
        });
        break;
      case 'featured':
        filtered = filtered.filter(provider => provider.isFeatured === true);
        break;
      default:
        break;
    }
    
    return filtered;
  }, [processedProviders, searchQuery, selectedCategory, selectedCommunity, selectedFilter]);

  // ======================================================
  // SORT PROVIDERS
  // ======================================================
  const sortedProviders = useMemo(() => {
    try {
      const sorted = [...filteredProviders];
      
      // If nearest filter is active, we already sorted by distance
      // But if user manually selected a different sort, respect that
      if (selectedFilter === 'nearest') {
        // Already sorted by distance in the filter step
        return sorted;
      }
      
      switch(sortBy) {
        case 'rating':
          return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        case 'distance':
          return sorted.sort((a, b) => {
            const distA = a.distance || 999999;
            const distB = b.distance || 999999;
            return distA - distB;
          });
        case 'experience':
          return sorted.sort((a, b) => (b.yearsOfExperience || 0) - (a.yearsOfExperience || 0));
        case 'price_low':
          return sorted.sort((a, b) => (a.hourlyRate || 0) - (b.hourlyRate || 0));
        case 'price_high':
          return sorted.sort((a, b) => (b.hourlyRate || 0) - (a.hourlyRate || 0));
        default:
          return sorted;
      }
    } catch (error) {
      console.warn('Error sorting providers:', error);
      return filteredProviders;
    }
  }, [filteredProviders, sortBy, selectedFilter]);

  // ======================================================
  // LOAD PROVIDERS USING getAllProviders
  // ======================================================
  const loadProviders = useCallback(async (params = {}) => {
    try {
      const searchParams = {
        page: params.page || 1,
        limit: params.limit || 100,
        sortBy: params.sortBy || 'createdAt',
        sortOrder: params.sortOrder || 'desc',
        ...params
      };

      // If there's a search query, use advanced search
      if (searchQuery?.trim()) {
        searchParams.query = searchQuery.trim();
        console.log('🔍 Searching providers with query:', searchParams.query);
        const result = await dispatch(advancedSearch(searchParams)).unwrap();
        return result;
      }

      // If there's a category selected, filter by service type
      const currentCategory = params.serviceType !== undefined 
        ? params.serviceType 
        : selectedCategory;
      
      if (currentCategory) {
        searchParams.serviceType = currentCategory;
        console.log('🔍 Searching providers with category:', currentCategory);
        const result = await dispatch(advancedSearch(searchParams)).unwrap();
        return result;
      }

      // Default: Get all providers using getAllProviders
      console.log('📌 Loading all providers with params:', searchParams);
      const result = await dispatch(getAllProviders(searchParams)).unwrap();

      const items = result?.data?.items || result?.data?.providers || [];
      console.log(`✅ Loaded ${items.length} providers`);

      return result;

    } catch (error) {
      console.error('❌ Load providers error:', error);
      return null;
    }
  }, [dispatch, searchQuery, selectedCategory]);

  // ======================================================
  // INITIAL LOAD - Use getAllProviders
  // ======================================================
  useEffect(() => {
    const init = async () => {
      setIsInitialLoad(true);
      try {
        // Load all providers on initial load
        const result = await dispatch(getAllProviders({
          page: 1,
          limit: 100,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })).unwrap();
        
        console.log('✅ Initial load complete:', result?.data?.items?.length || 0, 'providers');
      } catch (err) {
        console.error('❌ Initial load failed:', err);
      }
      setIsInitialLoad(false);
    };
    init();

    // Cleanup on unmount
    return () => {
      dispatch(resetSearchState());
    };
  }, []); // Empty dependency array - runs once on mount

  // ======================================================
  // HANDLERS
  // ======================================================
const handleDirection = (provider) => {
  if (!provider) {
    alert('Provider information is not available.');
    return;
  }

  const coords = getProviderCoordinates(provider);

  // ----------------------------------------------------
  // Build a complete readable address
  // ----------------------------------------------------
  const address = provider.address || {};

  const addressParts = [
    address.street,
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.province,
    address.zipCode,
    address.postalCode,
    address.country
  ].filter(Boolean);

  let fullAddress = addressParts.join(', ').trim();

  // If formatAddress() produces a better address, use it
  if (!fullAddress && provider.address) {
    try {
      fullAddress = formatAddress(provider.address);
    } catch (error) {
      console.warn('Could not format provider address:', error);
    }
  }

  // ----------------------------------------------------
  // Remove accidental duplicate commas/spaces
  // ----------------------------------------------------
  if (fullAddress) {
    fullAddress = fullAddress
      .replace(/\s+/g, ' ')
      .replace(/,\s*,+/g, ',')
      .trim();
  }

  console.log('📍 Provider address:', fullAddress);
  console.log('📍 Provider coordinates:', coords);

  // ----------------------------------------------------
  // PREFERRED:
  // Search Google Maps using the actual address.
  // This makes Google Maps display the provider address.
  // ----------------------------------------------------
  if (fullAddress) {
    const googleMapsUrl =
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

    console.log('🗺️ Opening Google Maps with address:', googleMapsUrl);

    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  // ----------------------------------------------------
  // FALLBACK:
  // If there is no address, use coordinates.
  // ----------------------------------------------------
  if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
    const googleMapsUrl =
      `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;

    console.log('🗺️ Opening Google Maps with coordinates:', googleMapsUrl);

    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  // ----------------------------------------------------
  // Nothing available
  // ----------------------------------------------------
  alert('Provider address and location are not available.');
};

  const handleProviderPress = (provider) => {
    navigate(`/provider-details/${provider?._id}`, { state: { provider } });
  };

  const handleBack = () => {
    try {
      navigate(-1);
    } catch (error) {
      console.warn('Error navigating back:', error);
    }
  };

  const handleSearch = () => {
    loadProviders({ page: 1 });
  };

  const handleCategoryChange = useCallback((category) => {
    console.log('🏷️ Category change:', selectedCategory, '→', category);
    setSelectedCategory(category);
    if (category) {
      // If category selected, use advanced search with service type
      loadProviders({
        page: 1,
        serviceType: category,
      });
    } else {
      // If no category, load all providers
      loadProviders({
        page: 1,
      });
    }
  }, [loadProviders, selectedCategory]);

  const handleFilterChange = (filterId) => {
    setSelectedFilter(filterId);
    
    if (filterId === 'nearest') {
      // When nearest filter is selected, set sortBy to distance as well
      setSortBy('distance');
    }
    
    // Don't close modal here - let the Apply Filters button handle it
  };

  const handleSortChange = (sortId) => {
    setSortBy(sortId);
    setShowSortModal(false);
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoading && !isInitialLoad) {
      const nextPage = (pagination.page || 1) + 1;
      loadProviders({ page: nextPage });
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await loadProviders({ page: 1 });
    setRefreshing(false);
  };

  // ======================================================
  // NEW: APPLY FILTERS HANDLER
  // ======================================================
  const handleApplyFilters = useCallback(() => {
    // Apply the current filter selections
    if (selectedFilter === 'nearest') {
      setSortBy('distance');
    }
    
    // Refresh the provider list with current filters
    const filterParams = {};
    
    if (selectedFilter === 'top_rated') {
      filterParams.minRating = MIN_RATING_FOR_TOP_RATED;
    } else if (selectedFilter === 'featured') {
      filterParams.isFeatured = true;
    } else if (selectedFilter === 'nearest') {
      // For nearest, we just keep the current sort
    }
    
    if (selectedCategory) {
      filterParams.serviceType = selectedCategory;
    }
    
    // Reload providers with filters
    loadProviders({ 
      page: 1, 
      ...filterParams 
    });
    
    // Close the modal
    setShowFilterModal(false);
  }, [selectedFilter, selectedCategory, loadProviders]);

// ======================================================
// NEW: RESET ALL FILTERS HANDLER - FIXED
// ======================================================
const handleResetFilters = useCallback(async () => {
  // First, reset all local state
  setSelectedCategory(null);
  setSelectedFilter('all');
  setSelectedCommunity('all');
  setSortBy('distance');
  setSearchQuery('');
  
  // Clear all filters in Redux
  dispatch(clearFilters());
  
  // Reset pagination
  setPage(1);
  
  // Close the modal
  setShowFilterModal(false);
  
  // Clear any cached results
  dispatch(clearResults());
  
  // Reload all providers with a clean slate
  try {
    const result = await dispatch(getAllProviders({
      page: 1,
      limit: 100,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    })).unwrap();
    
    console.log('✅ Reset complete, loaded:', result?.data?.items?.length || 0, 'providers');
  } catch (error) {
    console.error('❌ Reset failed:', error);
  }
}, [dispatch]);

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedCategory) count++;
    if (selectedFilter !== 'all') count++;
    if (selectedCommunity !== 'all') count++;
    if (sortBy !== 'distance') count++;
    return count;
  };

  const getCategoryIcon = (category) => {
    return categoryIcons[category] || '📋';
  };

  // Show loading state
  if (locationLoading && !isLocationReady) {
    return (
      <div className={styles.providersPage}>
        <div className={styles.loadingContainer}>
          <Loader2 size={40} className={styles.loadingSpinner} />
          <p>Loading location...</p>
        </div>
      </div>
    );
  }

  // ======================================================
  // MAIN RENDER
  // ======================================================
  return (
    <div className={styles.providersPage}>
      {/* Header */}
      <div className={styles.providersHeader}>
        <div className={styles.headerContent}>
          <button className={styles.backButton} onClick={handleBack}>
            <ChevronLeft size={24} />
          </button>
          <h1 className={styles.headerTitle}>{findTechnicians}</h1>
          {locationString && (
            <div className={styles.locationBadge}>
              <MapPin size={16} />
              <span>{locationString}</span>
            </div>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className={styles.searchSection}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} size={20} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              <X size={20} />
            </button>
          )}
          <button className={styles.filterToggle} onClick={() => setShowFilterModal(true)}>
            <Filter size={20} />
            {getActiveFilterCount() > 0 && (
              <span className={styles.filterCount}>{getActiveFilterCount()}</span>
            )}
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className={styles.categoriesSection}>
        <div className={styles.categoriesScroll}>
          {displayedCategories.map((category) => (
            <CategoryChip
              key={category}
              category={category}
              selectedCategory={selectedCategory}
              setSelectedCategory={handleCategoryChange}
              providerCount={categoryCounts[category] || 0}
            />
          ))}
          {serviceTypes.length > DISPLAY_CATEGORIES_COUNT && (
            <button
              className={`${styles.categoryChip} ${styles.showMoreButton}`}
              onClick={() => setShowAllCategories(!showAllCategories)}
            >
              <span className={styles.categoryChipEmoji}>📂</span>
              <span className={styles.categoryChipText}>
                {showAllCategories ? showLess : `${moreCategories} (${serviceTypes.length - DISPLAY_CATEGORIES_COUNT})`}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Results Header */}
      <div className={styles.resultsHeader}>
        <div className={styles.resultsContent}>
          <span className={styles.resultsCount}>
            {sortedProviders.length === 1
              ? `${sortedProviders.length} ${techniciansFound}`
              : `${sortedProviders.length} ${techniciansFounds}`}
            {selectedFilter === 'nearest' && ipLocation && (
              <span className={styles.countryIndicator}> • Near you</span>
            )}
            {selectedFilter !== 'nearest' && ipLocation && (
              <span className={styles.countryIndicator}> • {ipLocation.country}</span>
            )}
          </span>
          <button className={styles.sortToggle} onClick={() => setShowSortModal(true)}>
            <SortAsc size={18} />
            <span>{sortByLabel}: {sortOptions.find(s => s.id === sortBy)?.label}</span>
          </button>
        </div>
      </div>

      {/* Providers Grid */}
      <div className={styles.providersGridSection}>
        {isLoading && isInitialLoad ? (
          <div className={styles.loadingContainer}>
            <Loader2 size={40} className={styles.loadingSpinner} />
            <p>Loading providers...</p>
          </div>
        ) : sortedProviders.length > 0 ? (
          <div className={styles.providersGrid}>
            {sortedProviders.map((provider) => {
              const key = provider._id || provider.id;
              return (
                <ProviderCard 
                  key={key}
                  item={provider} 
                  onPress={handleProviderPress}
                  onDirection={handleDirection}
                />
              );
            })}
            {hasMore && !isLoading && (
              <div className={styles.loadMoreContainer}>
                <button className={styles.loadMoreButton} onClick={handleLoadMore}>
                  Load More
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            {selectedFilter === 'nearest' ? (
              <>
                <MapPin size={64} className={styles.emptyIcon} />
                <h3 className={styles.emptyTitle}>No providers near you</h3>
                <p className={styles.emptySubtitle}>
                  Try adjusting the distance filter or select "All" to see all providers
                </p>
              </>
            ) : selectedCategory ? (
              <>
                <Wrench size={64} className={styles.emptyIcon} />
                <h3 className={styles.emptyTitle}>No {selectedCategory} providers found</h3>
                <p className={styles.emptySubtitle}>
                  Try selecting a different category or adjust your search
                </p>
              </>
            ) : ipLocation ? (
              <>
                <Globe size={64} className={styles.emptyIcon} />
                <h3 className={styles.emptyTitle}>No providers in {ipLocation.country}</h3>
                <p className={styles.emptySubtitle}>
                  We couldn't find any providers in your country. Try adjusting your search.
                </p>
              </>
            ) : (
              <>
                <Search size={64} className={styles.emptyIcon} />
                <h3 className={styles.emptyTitle}>{noTechniciansFound}</h3>
                <p className={styles.emptySubtitle}>{adjustSearch}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className={styles.modalOverlay} onClick={() => setShowFilterModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{filtersLabel}</h2>
              <button className={styles.modalClose} onClick={() => setShowFilterModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.filterSection}>
                <h3 className={styles.filterSectionTitle}>{allCommunities}</h3>
                <div className={styles.filterOptions}>
                  {communityOptions.map(option => (
                    <button
                      key={option.id}
                      className={`${styles.filterOption} ${selectedCommunity === option.id ? styles.active : ''}`}
                      onClick={() => setSelectedCommunity(option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.filterSection}>
                <h3 className={styles.filterSectionTitle}>{filtersLabel}</h3>
                <div className={styles.filterOptions}>
                  {filterOptions.map(option => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        className={`${styles.filterOption} ${selectedFilter === option.id ? styles.active : ''}`}
                        onClick={() => handleFilterChange(option.id)}
                      >
                        <Icon size={16} />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Filter Actions - Apply and Reset buttons */}
              <div className={styles.filterActions}>
                <button 
                  className={`${styles.filterActionButton} ${styles.resetFiltersButton}`}
                  onClick={handleResetFilters}
                >
                  {resetFilters}
                </button>
                <button 
                  className={`${styles.filterActionButton} ${styles.applyFiltersButton}`}
                  onClick={handleApplyFilters}
                >
                  {applyFilters}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sort Modal */}
      {showSortModal && (
        <div className={styles.modalOverlay} onClick={() => setShowSortModal(false)}>
          <div className={`${styles.modalContent} ${styles.sortModal}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{sortByLabel}</h2>
              <button className={styles.modalClose} onClick={() => setShowSortModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {sortOptions.map(option => (
                <button
                  key={option.id}
                  className={`${styles.sortOption} ${sortBy === option.id ? styles.active : ''}`}
                  onClick={() => handleSortChange(option.id)}
                >
                  <span>{option.label}</span>
                  {sortBy === option.id && (
                    <CheckCircle size={20} color={colors.primary} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}. {madeWithLove}
            </div> 
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProvidersPage;